import { type UserDocument } from '@hooks/use-ocr.hooks'

type MobileSteps =
	| 'SELEZIONE'
	| 'UPLOAD_ID'
	| 'UPLOAD_FISCAL'
	| 'UPLOAD_REDD'
	| 'UPLOAD_CUD'
	| 'UPLOAD_SUCCESS'
	| 'REVIEW_DATA'
	| 'SUCCESS'
	| 'SESSION_ERROR'
	| 'SESSION_EXPIRED'
	| 'SESSION_PC'

type MobilePhotoSide = {
	content: File | null
	errorMessage: string
}

type MobileUpload = {
	documents: Record<string, MobilePhotoSide>
	optional: boolean
	ocrScan: boolean
	valid: boolean
	uploaded: boolean
	error: boolean
	id: UserDocument
	title: string
	icon: string
	navigationID: MobileSteps
}

type MobileState = {
	currentStep: MobileSteps
	mobileSession: string
	isSessionActive: boolean
	idRichiesta: number
	shouldUseOCR: boolean
	shouldRefreshState: boolean
	steps: Record<Exclude<UserDocument, 'X'>, MobileUpload>
	userData: {
		Name: string | null
		Surname: string | null
		FiscalCode: string | null
	}
}

const defaultMobileState: MobileState = {
	currentStep: 'SELEZIONE',
	shouldUseOCR: true,
	mobileSession: '',
	idRichiesta: 0,
	isSessionActive: false,
	shouldRefreshState: true,
	steps: {} as MobileState['steps'],
	userData: {
		Name: null,
		Surname: null,
		FiscalCode: null,
	},
}

type Actions =
	| { type: 'SET_SESSION'; payload: string }
	| { type: 'MOVE_STEP'; payload: MobileSteps }
	| {
			type: 'SET_PHOTO'
			payload: {
				step: keyof MobileState['steps']
				file: File | null
				documentSide: keyof MobileUpload['documents']
			}
	  }
	| {
			type: 'SET_UPLOAD_VALID'
			payload: { step: keyof MobileState['steps']; valid: boolean }
	  }
	| {
			type: 'SET_UPLOAD_STATE'
			payload: { step: keyof MobileState['steps']; done: boolean }
	  }
	| {
			type: 'SET_ID_RICHIESTA'
			payload: number
	  }
	| {
			type: 'SET_MOBILE_STATUS'
			payload: boolean
	  }
	| {
			type: 'SET_OCR_MODE'
			payload: boolean
	  }
	| {
			type: 'SET_DYNAMIC_STEPS'
			payload: Record<string, MobileUpload>
	  }
	| {
			type: 'ADD_DOCUMENT_PAGE'
			payload: keyof MobileState['steps']
	  }
	| {
			type: 'DELETE_LATEST_DOCUMENT_PAGE'
			payload: keyof MobileState['steps']
	  }
	| {
			type: 'SET_REFRESH'
			payload: boolean
	  }

function reducer(state: MobileState, action: Actions): MobileState {
	switch (action.type) {
		case 'SET_SESSION':
			return {
				...state,
				mobileSession: action.payload,
			}
		case 'SET_REFRESH':
			return {
				...state,
				shouldRefreshState: action.payload,
			}
		case 'SET_DYNAMIC_STEPS':
			return {
				...state,
				steps: action.payload,
			}
		case 'DELETE_LATEST_DOCUMENT_PAGE': {
			const stateDocs = Object.entries(state.steps[action.payload].documents)
			const documents = Object.fromEntries(
				stateDocs.at(-1)?.[1].content !== null
					? stateDocs
					: stateDocs.slice(0, -1)
			)

			return {
				...state,
				steps: {
					...state.steps,
					[action.payload]: {
						...state.steps[action.payload],
						documents,
					},
				},
			}
		}
		case 'ADD_DOCUMENT_PAGE': {
			const pagesAvailable = Object.keys(
				state.steps[action.payload].documents
			).length
			return {
				...state,
				steps: {
					...state.steps,
					[action.payload]: {
						...state.steps[action.payload],
						documents: {
							...state.steps[action.payload].documents,
							[`Document_${pagesAvailable + 1}`]: {
								errorMessage: '',
								content: null,
							},
						},
					},
				},
			}
		}
		case 'SET_OCR_MODE':
			return {
				...state,
				shouldUseOCR: action.payload,
			}
		case 'SET_MOBILE_STATUS':
			return {
				...state,
				isSessionActive: action.payload,
			}
		case 'SET_ID_RICHIESTA':
			return {
				...state,
				idRichiesta: action.payload,
			}
		case 'MOVE_STEP': {
			return {
				...state,
				currentStep: action.payload,
			}
		}
		case 'SET_UPLOAD_VALID': {
			const { step, valid } = action.payload
			return {
				...state,
				steps: {
					...state.steps,
					[step]: {
						...state.steps[step],
						valid,
					},
				},
			}
		}
		case 'SET_UPLOAD_STATE': {
			const { step, done } = action.payload
			return {
				...state,
				steps: {
					...state.steps,
					[step]: {
						...state.steps[step],
						uploaded: done,
					},
				},
			}
		}
		case 'SET_PHOTO': {
			const { step, file, documentSide } = action.payload

			return {
				...state,
				steps: {
					...state.steps,
					[step]: {
						...state.steps[step],
						documents: {
							...state.steps[step].documents,
							[documentSide]: {
								...state.steps[step].documents[documentSide],
								content: file,
							},
						},
					},
				},
			}
		}
		default:
			return state
	}
}

export type { MobileState, MobileSteps, Actions, MobileUpload }
export { reducer, defaultMobileState }
